/**
 * `ChatEmptyState` — full-bleed welcome surface shown when the
 * current thread has no messages yet.
 *
 * 15 scenarios are grouped into three categories that mirror how a
 * founder or operator naturally thinks about the business:
 *
 *   - "Check on my business" — read-only queries the agent can answer
 *     from any state of the books, even an empty database.
 *   - "Record money in" — sales-side writes: issue invoice, take payment.
 *   - "Record money out" — purchase-side writes: log a vendor bill,
 *     record payment to supplier, manage master data.
 *
 * Every prompt below is self-contained — it includes enough context
 * (amounts, names, units, GST rates) for the agent to execute end-to-end
 * without requiring follow-up clarification. New customers, vendors, or
 * items are created on the fly if they are not yet in the system.
 *
 * Edit `SCENARIO_GROUPS` when new tools land (keep the category balance).
 */
import {
  ArrowDownToLineIcon,
  ArrowUpFromLineIcon,
  BarChart3Icon,
  BotIcon,
  BuildingIcon,
  CircleDollarSignIcon,
  ClipboardListIcon,
  FileTextIcon,
  GaugeIcon,
  PackageIcon,
  ReceiptTextIcon,
  SearchIcon,
  SparklesIcon,
  TruckIcon,
  UserPlusIcon,
  type LucideIcon,
} from "lucide-react";

interface Scenario {
  icon: LucideIcon;
  title: string;
  prompt: string;
}

interface ScenarioGroup {
  label: string;
  scenarios: Scenario[];
}

const SCENARIO_GROUPS: ScenarioGroup[] = [
  {
    label: "Check on my business",
    scenarios: [
      {
        icon: GaugeIcon,
        title: "Today's business snapshot",
        prompt:
          "Give me a quick snapshot of my business today — total receivables outstanding, how much is overdue, what I collected this month, and the one thing I should act on first.",
      },
      {
        icon: ReceiptTextIcon,
        title: "Who owes me money?",
        prompt:
          "Show me all overdue invoices — customer name, invoice number, amount, and how many days overdue. Who should I follow up with first?",
      },
      {
        icon: CircleDollarSignIcon,
        title: "What do I owe suppliers?",
        prompt:
          "List all my unpaid vendor bills — which suppliers I owe, how much, and which are past due date. Total up what I need to clear this week.",
      },
      {
        icon: BarChart3Icon,
        title: "Top customers by business",
        prompt:
          "Which customers have given me the most business this year? Show concentration — who is the biggest share of my receivables and total invoiced.",
      },
      {
        icon: SparklesIcon,
        title: "End-of-day briefing",
        prompt:
          "Give me a short end-of-day briefing: what came in today, what payments I need to chase, any bills due this week, and three things to do tomorrow.",
      },
    ],
  },
  {
    label: "Record money coming in",
    scenarios: [
      {
        icon: FileTextIcon,
        title: "Issue an invoice",
        prompt:
          "Issue an invoice for Arora Fabrics — 200 metres of Cotton Fabric at ₹280 per metre, 5% GST, payment due in 30 days, place of supply Maharashtra. Create the customer and item if they are not in the system yet.",
      },
      {
        icon: ArrowDownToLineIcon,
        title: "Customer paid — settle invoice",
        prompt:
          "Arora Fabrics transferred ₹58,800 by NEFT today. Record this payment and settle it against their latest open invoice.",
      },
      {
        icon: SearchIcon,
        title: "Find invoices for a customer",
        prompt:
          "Show me all invoices raised for Arora Fabrics — document number, amount, due date, and current payment status. Which ones are still open?",
      },
      {
        icon: UserPlusIcon,
        title: "Add a new customer",
        prompt:
          "Add a new customer — Patel Exports Pvt Ltd, GSTIN 24AABCP5678Q1Z4, contact Vikram Patel, phone 9845012234, city Mumbai, state Maharashtra.",
      },
      {
        icon: ClipboardListIcon,
        title: "List invoices this month",
        prompt:
          "Show me all invoices raised this month — total count, total value, how many are paid vs outstanding, and flag anything due in the next 7 days.",
      },
    ],
  },
  {
    label: "Record money going out",
    scenarios: [
      {
        icon: ReceiptTextIcon,
        title: "Record a vendor bill",
        prompt:
          "Record a bill from Sundaram Threads — ₹36,000 for yarn supply + 12% GST (total ₹40,320), their invoice number SUTR-2024-441, received today, payment due in 21 days. Create the vendor if not already in the system.",
      },
      {
        icon: ArrowUpFromLineIcon,
        title: "Record payment to supplier",
        prompt:
          "I paid Sundaram Threads ₹40,320 by bank transfer today (reference TXN-88432). Record this as a payment against their outstanding bill.",
      },
      {
        icon: BuildingIcon,
        title: "Add a new supplier",
        prompt:
          "Add a new supplier — Mehta Dyes and Chemicals, GSTIN 24AABCM5678Q1Z7, contact 9876543210, based in Ahmedabad Gujarat. Payment terms 30 days.",
      },
      {
        icon: PackageIcon,
        title: "Add a product to catalog",
        prompt:
          "Add Polyester Blend Fabric to my product catalog — selling price ₹340 per metre, HSN code 5407, 12% GST slab. Also set purchase rate ₹280 per metre.",
      },
      {
        icon: TruckIcon,
        title: "All pending vendor bills",
        prompt:
          "List all my pending vendor bills that haven't been paid yet — supplier name, bill number, amount, and days until (or past) due date.",
      },
    ],
  },
];

interface ChatEmptyStateProps {
  onSelect: (prompt: string) => void;
}

export function ChatEmptyState({ onSelect }: ChatEmptyStateProps) {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10 xl:max-w-4xl">
      {/* Header */}
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="grid size-12 place-items-center rounded-2xl bg-sky-50 text-sky-700 shadow-sm ring-1 ring-sky-100">
          <BotIcon className="h-6 w-6" aria-hidden />
        </div>
        <h2 className="mt-5 text-2xl font-semibold tracking-tight text-ink">
          What would you like to do?
        </h2>
        <p className="mt-2 max-w-md text-sm leading-6 text-muted">
          Ask in plain English. Flint reads your books, records transactions, and answers questions
          — no forms needed.
        </p>
      </div>

      {/* Scenario groups */}
      <div className="space-y-6">
        {SCENARIO_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-muted">
              {group.label}
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {group.scenarios.map((scenario) => (
                <button
                  key={scenario.title}
                  type="button"
                  onClick={() => onSelect(scenario.prompt)}
                  className="group flex items-start gap-3 rounded-xl border border-line bg-paper px-3.5 py-3 text-left transition-all hover:border-sky-300 hover:shadow-card focus-visible:border-sky-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-100"
                >
                  <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-bg text-ink-2 transition-colors group-hover:bg-sky-50 group-hover:text-sky-700">
                    <scenario.icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-5 text-ink">
                      {scenario.title}
                    </span>
                    <span className="mt-0.5 block text-xs leading-4 text-muted line-clamp-2">
                      {scenario.prompt}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
